'use client';

import {
  ReactNode, useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@utils/cn';
import { Bars3Icon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { TwoColumnLayoutContext } from './TwoColumnLayoutContext';
import {
  SIDEBAR_WIDTH_MOBILE,
  SIDEBAR_WIDTH_OPEN,
  SIDEBAR_WIDTH_OPEN_CLASS,
  SIDEBAR_WIDTH_COLLAPSED_CLASS,
  MAIN_OFFSET_OPEN_CLASS,
  MAIN_OFFSET_COLLAPSED_CLASS,
} from './constants';

interface TwoColumnLayoutProps {
  leftSidebar?: ReactNode;
  mainContent: ReactNode;
  rightSidebar?: ReactNode;
  showLeftToggle?: boolean;
  className?: string;
}

export const TwoColumnLayout = (props: TwoColumnLayoutProps) => {
  const {
    leftSidebar,
    mainContent,
    rightSidebar,
    showLeftToggle = true,
    className,
  } = props;

  // Desktop sidebar starts open, mobile starts closed
  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  const sidebarRef = useRef<HTMLElement>(null);
  const toggleLeftSidebar = useCallback(() => {
    setIsLeftOpen((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({ isLeftOpen, toggleLeftSidebar }),
    [isLeftOpen, toggleLeftSidebar],
  );

  // Detect if this layout is rendered inside a modal (e.g. job detail opened from MC).
  // When inside a modal, the sidebar must NOT be portaled to body because the modal
  // overlay (z-[70]) would cover the portaled sidebar (z-[60]). Rendering inline
  // keeps the sidebar inside the modal's DOM layer.
  const [isInsideModal, setIsInsideModal] = useState(false);
  useEffect(() => {
    setMounted(true);
    setIsInsideModal(!!document.querySelector('[role="dialog"]'));
  }, []);

  // Notify listeners (e.g. MainNavbar) of the current sidebar state.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sidebar-state-change', { detail: { isOpen: isLeftOpen } }));
  }, [isLeftOpen]);

  // Collapse sidebar when switching to mobile view
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        setIsLeftOpen(false);
      } else {
        setIsLeftOpen(true);
      }
    };

    // Set initial state based on screen size
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setIsLeftOpen(false);
    }

    // Only listen to resize events, not state changes
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array - only run once on mount

  // Close sidebar on mobile with Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLeftOpen && window.innerWidth < 1024) {
        setIsLeftOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLeftOpen]);

  // Listen for external toggle-sidebar / close-sidebar events
  useEffect(() => {
    const handleToggle = () => toggleLeftSidebar();
    const handleClose = () => setIsLeftOpen(false);
    window.addEventListener('toggle-sidebar', handleToggle);
    window.addEventListener('close-sidebar', handleClose);
    return () => {
      window.removeEventListener('toggle-sidebar', handleToggle);
      window.removeEventListener('close-sidebar', handleClose);
    };
  }, [toggleLeftSidebar]);
  // Close sidebar on mobile when tapping outside the sidebar element (works even through Modal overlays)
  useEffect(() => {
    if (!isLeftOpen) return undefined;
    const handleOutsideTouch = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      // Skip if the tap is on the toggle button (it handles its own toggle via the toggle-sidebar event)
      if (target && (target as HTMLElement).closest('[data-sidebar-toggle]')) return;
      if (window.innerWidth < 1024 && sidebarRef.current && target && !sidebarRef.current.contains(target as Node)) {
        setIsLeftOpen(false);
      }
    };
    document.addEventListener('touchend', handleOutsideTouch);
    return () => {
      document.removeEventListener('touchend', handleOutsideTouch);
    };
  }, [isLeftOpen]);


  // Prevent body scroll on mobile when sidebar is open
  // Only apply when not inside a modal (re-checked each time since a modal can open after mount)
  useEffect(() => {
    const hasOpenDialog = !!document.querySelector('[role="dialog"]');
    if (window.innerWidth < 1024 && isLeftOpen && !hasOpenDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLeftOpen]);

  const hasLeftSidebar = !!leftSidebar;
  const hasRightSidebar = !!rightSidebar;

  // Keep the globally-mounted header aligned with the main content start.
  // useLayoutEffect so the CSS variable is applied before the browser paints —
  // prevents a flash of the floating navbar centered on the wrong origin.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const applyOffset = (offset: string) => {
      if (offset) {
        root.style.setProperty('--two-column-header-offset', offset);
      } else {
        root.style.removeProperty('--two-column-header-offset');
      }
      window.dispatchEvent(new CustomEvent('two-column-header-offset-change', {
        detail: { offset },
      }));
    };

    const updateHeaderOffset = () => {
      if (!hasLeftSidebar) {
        applyOffset('');
        return;
      }

      const isMobile = window.innerWidth < 1024;
      let offset = '';

      if (isLeftOpen) {
        offset = isMobile ? SIDEBAR_WIDTH_MOBILE : SIDEBAR_WIDTH_OPEN;
      } else if (!isMobile) {
        offset = '64px';
      }

      applyOffset(offset);
    };

    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    return () => {
      window.removeEventListener('resize', updateHeaderOffset);
      applyOffset('');
    };
  }, [hasLeftSidebar, isLeftOpen]);

  return (
    <TwoColumnLayoutContext.Provider value={contextValue}>
      <div className={cn('two-column-with-sidebar relative flex h-[calc(100dvh-var(--header-height,80px))] overflow-hidden', className)}>
        {/* Mobile overlay backdrop — gated on `mounted` so the SSR default
            `isLeftOpen: true` doesn't paint the overlay before the resize effect
            collapses it on mobile. */}
        {mounted && isLeftOpen && (
          <div
            className="pointer-events-auto fixed inset-0 z-[55] bg-ink/20 backdrop-blur-sm transition-opacity lg:hidden"
            style={{ left: SIDEBAR_WIDTH_MOBILE, right: 0 }}
            onClick={() => setIsLeftOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Left sidebar — normally portaled to document.body to bypass HeadlessUI's
             useInertOthers. When inside a modal, render inline so the sidebar stays
             within the modal's z-index layer. Sidebar z-[80] sits above modal z-[70]
             so the MC sidebar stays visible when a modal (e.g. the CV modal opened
             from the applicants flow) is open on top of the layout. CvDetailHub
             reserves the same desktop offset via the TwoColumnLayout context, so
             the modal content sits to the right of the sidebar instead of being
             covered. */}
        {hasLeftSidebar && mounted && (() => {
          const sidebarElement = (
            <aside
              ref={sidebarRef}
              className={cn(
                'fixed left-0 z-[80] transform bg-surface transition-all duration-300 ease-in-out',
                // Mobile: hidden when closed, full width when open
                isLeftOpen ? 'w-[280px] translate-x-0' : '-translate-x-full lg:translate-x-0',
                // Desktop: collapsed or expanded — widths come from TwoColumnLayout/constants
                'lg:z-[80]',
                isLeftOpen ? SIDEBAR_WIDTH_OPEN_CLASS : SIDEBAR_WIDTH_COLLAPSED_CLASS,
              )}
              style={{
                top: 0,
                height: isInsideModal ? '100%' : '100dvh',
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div
                className="flex h-full flex-col"
                onClick={(e) => {
                  if (window.innerWidth < 1024 && isLeftOpen && (e.target as HTMLElement).closest('a')) {
                    setIsLeftOpen(false);
                  }
                }}
              >
                {/* Hamburger button inside sidebar */}
                {showLeftToggle && (
                  <div className="flex items-center justify-between px-4 py-2">
                    <button
                      type="button"
                      onClick={toggleLeftSidebar}
                      className={cn(
                        'flex size-10 items-center justify-center rounded-lg transition-colors',
                        'hover:bg-surface-200',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      )}
                      aria-label={isLeftOpen ? 'Close sidebar' : 'Open sidebar'}
                      aria-expanded={isLeftOpen}
                    >
                      <Bars3Icon className="size-6 text-ink" />
                    </button>

                    {isLeftOpen && (
                      <button
                        type="button"
                        onClick={toggleLeftSidebar}
                        className={cn(
                          'flex size-10 items-center justify-center rounded-lg transition-colors',
                          'hover:bg-surface-200',
                          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                        )}
                        aria-label="Collapse sidebar"
                      >
                        <ChevronDoubleLeftIcon className="size-5 text-ink" />
                      </button>
                    )}
                  </div>
                )}

                {/* Sidebar content */}
                <div className={cn(
                  'flex-1 overflow-hidden transition-opacity duration-300',
                  isLeftOpen ? 'opacity-100' : 'opacity-0 lg:opacity-0',
                )}
                >
                  {leftSidebar}
                </div>
              </div>
            </aside>
          );

          return isInsideModal ? sidebarElement : createPortal(sidebarElement, document.body);
        })()}

        {/* Main content */}
        <main
          className={cn(
            'flex-1 min-w-0 h-full overflow-y-auto transition-all duration-300',
            // Mobile: no sidebar offset (sidebar is hidden/overlay)
            'ml-0',
            // Desktop: account for sidebar width — values come from TwoColumnLayout/constants
            MAIN_OFFSET_COLLAPSED_CLASS,
            isLeftOpen && MAIN_OFFSET_OPEN_CLASS,
          )}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorY: 'contain',
            touchAction: 'pan-y',
          }}
        >
          {mainContent}
        </main>

        {/* Right sidebar (if needed in future) */}
        {hasRightSidebar && (
          <aside className="hidden bg-surface lg:block lg:w-[280px]">
            <div className="h-full overflow-y-auto">
              {rightSidebar}
            </div>
          </aside>
        )}
      </div>
    </TwoColumnLayoutContext.Provider>
  );
};
